import type { Form, FormListItem, FormSchema, FormSettings } from '../types/form';
import type { Submission, PresignedUploadResponse } from '../types/submission';

const API_BASE = '/api';

async function fetchAPI<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: 'Request failed' })) as { error?: string };
    throw new Error(errorData.error || `HTTP ${response.status}`);
  }

  return response.json();
}

// Forms API
export async function listForms(params?: {
  status?: string;
  page?: number;
  limit?: number;
}): Promise<{ forms: FormListItem[]; pagination: { page: number; limit: number; total: number; totalPages: number } }> {
  const searchParams = new URLSearchParams();
  if (params?.status) searchParams.set('status', params.status);
  if (params?.page) searchParams.set('page', String(params.page));
  if (params?.limit) searchParams.set('limit', String(params.limit));

  const query = searchParams.toString();
  return fetchAPI(`/forms${query ? `?${query}` : ''}`);
}

export async function getForm(id: string): Promise<Form> {
  return fetchAPI(`/forms/${id}`);
}

export async function createForm(data: { name: string; description?: string }): Promise<{ id: string; slug: string; name: string }> {
  return fetchAPI('/forms', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateForm(
  id: string,
  data: { name?: string; description?: string; schema?: FormSchema; settings?: FormSettings }
): Promise<{ success: boolean; version: number }> {
  return fetchAPI(`/forms/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteForm(id: string): Promise<{ success: boolean }> {
  return fetchAPI(`/forms/${id}`, {
    method: 'DELETE',
  });
}

export async function publishForm(id: string): Promise<{ success: boolean; slug: string; publicUrl: string; resultsUrl: string; resultsKey: string }> {
  return fetchAPI(`/forms/${id}/publish`, {
    method: 'POST',
  });
}

// Submissions API
export async function listSubmissions(
  formId: string,
  params?: { status?: string; page?: number; limit?: number }
): Promise<{ submissions: Submission[]; pagination: { page: number; limit: number; total: number; totalPages: number } }> {
  const searchParams = new URLSearchParams();
  if (params?.status) searchParams.set('status', params.status);
  if (params?.page) searchParams.set('page', String(params.page));
  if (params?.limit) searchParams.set('limit', String(params.limit));

  const query = searchParams.toString();
  return fetchAPI(`/forms/${formId}/submissions${query ? `?${query}` : ''}`);
}

export async function submitForm(
  formId: string,
  data: Record<string, unknown>,
  uploadIds?: string[]
): Promise<{ success: boolean; submissionId: string; message: string; redirectUrl?: string }> {
  return fetchAPI(`/forms/${formId}/submissions`, {
    method: 'POST',
    body: JSON.stringify({ data, uploadIds }),
  });
}

export async function exportSubmissions(
  formId: string,
  format: 'json' | 'csv' = 'json',
  status?: string
): Promise<Blob | object> {
  const searchParams = new URLSearchParams({ format });
  if (status) searchParams.set('status', status);

  const response = await fetch(`${API_BASE}/forms/${formId}/submissions/export?${searchParams}`);

  if (!response.ok) {
    throw new Error('Export failed');
  }

  if (format === 'csv') {
    return response.blob();
  }

  return response.json();
}

// File Upload API
export async function getPresignedUrl(data: {
  formId: string;
  fieldId: string;
  filename: string;
  contentType: string;
  size: number;
  fieldType?: string;
}): Promise<PresignedUploadResponse & { objectKey: string; uploadUrl: string }> {
  return fetchAPI('/upload/presign', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function uploadFile(
  uploadId: string,
  file: File,
  onProgress?: (percent: number) => void
): Promise<{ success: boolean; uploadId: string; path: string }> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', `${API_BASE}/upload/${uploadId}`);
    xhr.setRequestHeader('Content-Type', file.type);

    if (onProgress) {
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) {
          onProgress(Math.round((e.loaded / e.total) * 100));
        }
      };
    }

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(JSON.parse(xhr.responseText));
      } else {
        reject(new Error(`Upload failed: ${xhr.status}`));
      }
    };

    xhr.onerror = () => reject(new Error('Upload failed'));
    xhr.send(file);
  });
}

export function getFileUrl(uploadId: string): string {
  return `${API_BASE}/upload/${uploadId}`;
}

// Public Form API
export async function getPublicForm(slug: string): Promise<Form> {
  const response = await fetch(`/api/f/${slug}`);
  if (!response.ok) {
    throw new Error('Form not found');
  }
  return response.json();
}
